import { LIGHT_CAPTURE, NORMAL_CAPTURE, type StepCaptureProfile } from './capture-profile.ts'
import { ALL_WINDOW_REASONS, type WindowReason } from './effect-measure.ts'
import type { EffectMemory } from './effect-memory.ts'
import {
  type FrameShape,
  modelForShape,
  SEGMENTER_TIERS,
  type SegmenterModelId,
  type SegmenterPlatform,
} from './segmenter-catalog.ts'

export type PhoneCapture = 'normal' | 'light'

export type EffectDelegate = 'GPU' | 'CPU'

export const DESKTOP_STEP_IDS = [
  'multiclass_gpu',
  'selfie_gpu',
  'selfie_gpu_every_2',
  'selfie_cpu',
  'selfie_cpu_every_2',
] as const

export type DesktopStepId = (typeof DESKTOP_STEP_IDS)[number]

export interface PhoneStep {
  platform: 'phone'
  capture: PhoneCapture
  delegate: EffectDelegate
}

export interface DesktopStep {
  platform: 'desktop'
  id: DesktopStepId
}

export type EffectStep = PhoneStep | DesktopStep

export type EffectFloor = 'floor'

export const PHONE_STEP_IDS = ['normal_gpu', 'normal_cpu', 'light_gpu', 'light_cpu'] as const

export type EffectStepId = (typeof PHONE_STEP_IDS)[number] | DesktopStepId

const phoneStep = (capture: PhoneCapture, delegate: EffectDelegate): PhoneStep => ({
  platform: 'phone',
  capture,
  delegate,
})

const desktopStep = (id: DesktopStepId): DesktopStep => ({ platform: 'desktop', id })

export function stepId(step: EffectStep): EffectStepId {
  if (step.platform === 'desktop') return step.id
  return step.delegate === 'GPU' ? `${step.capture}_gpu` : `${step.capture}_cpu`
}

export function parseStepId(platform: SegmenterPlatform, value: unknown): EffectStep | null {
  if (platform === 'desktop') {
    const id = DESKTOP_STEP_IDS.find((candidate) => candidate === value)
    return id ? desktopStep(id) : null
  }
  switch (value) {
    case 'normal_gpu':
      return phoneStep('normal', 'GPU')
    case 'normal_cpu':
      return phoneStep('normal', 'CPU')
    case 'light_gpu':
      return phoneStep('light', 'GPU')
    case 'light_cpu':
      return phoneStep('light', 'CPU')
    default:
      return null
  }
}

export function stepDown(step: EffectStep): EffectStep | EffectFloor {
  if (step.platform === 'phone') {
    return step.capture === 'normal' ? phoneStep('light', step.delegate) : 'floor'
  }
  switch (step.id) {
    case 'multiclass_gpu':
      return desktopStep('selfie_gpu')
    case 'selfie_gpu':
      return desktopStep('selfie_gpu_every_2')
    case 'selfie_gpu_every_2':
      return 'floor'
    case 'selfie_cpu':
      return desktopStep('selfie_cpu_every_2')
    case 'selfie_cpu_every_2':
      return 'floor'
  }
}

export function lightestStep(platform: SegmenterPlatform): EffectStep {
  return platform === 'phone' ? phoneStep('light', 'GPU') : desktopStep('selfie_gpu_every_2')
}

export function startingStep(
  platform: SegmenterPlatform,
  memory: Pick<EffectMemory, 'step'> | null,
): EffectStep {
  if (memory && memory.step.platform === platform) return memory.step
  return platform === 'phone' ? phoneStep('light', 'GPU') : desktopStep('selfie_gpu')
}

export function candidateStep(step: EffectStep): EffectStep | null {
  if (step.platform === 'phone') {
    return step.capture === 'light' ? phoneStep('normal', step.delegate) : null
  }
  if (step.id === 'multiclass_gpu') return null
  return step.id === 'selfie_cpu' || step.id === 'selfie_cpu_every_2'
    ? desktopStep('selfie_gpu')
    : desktopStep('multiclass_gpu')
}

export function afterWrongOutput(step: EffectStep): EffectStep | EffectFloor {
  if (step.platform === 'phone') {
    return step.delegate === 'GPU' ? phoneStep(step.capture, 'CPU') : 'floor'
  }
  switch (step.id) {
    case 'multiclass_gpu':
    case 'selfie_gpu':
    case 'selfie_gpu_every_2':
      return desktopStep('selfie_cpu')
    case 'selfie_cpu':
    case 'selfie_cpu_every_2':
      return 'floor'
  }
}

export function stepModel(step: EffectStep, shape: FrameShape): SegmenterModelId {
  if (step.platform === 'desktop' && step.id === 'multiclass_gpu') return SEGMENTER_TIERS.heavy
  return modelForShape(shape)
}

export function stepDelegate(step: EffectStep): EffectDelegate {
  if (step.platform === 'phone') return step.delegate
  return step.id === 'selfie_cpu' || step.id === 'selfie_cpu_every_2' ? 'CPU' : 'GPU'
}

export function stepSegmentationInterval(step: EffectStep): number {
  if (step.platform === 'phone') return step.capture === 'light' ? 2 : 1
  return step.id === 'selfie_gpu_every_2' || step.id === 'selfie_cpu_every_2' ? 2 : 1
}

export function prejoinStepDown(
  step: EffectStep,
  reasons: readonly WindowReason[],
): EffectStep | EffectFloor {
  if (step.platform === 'desktop' && step.id === 'selfie_gpu_every_2' && reasons.includes('cost')) {
    return desktopStep('selfie_cpu')
  }
  return stepDown(step)
}

export function nextUntried(
  step: EffectStep,
  failed: ReadonlySet<EffectStepId>,
  reasons: readonly WindowReason[],
): EffectStep | EffectFloor {
  let next = prejoinStepDown(step, reasons)
  while (next !== 'floor' && failed.has(stepId(next))) next = stepDown(next)
  return next
}

const COST_ONLY: readonly WindowReason[] = ['cost']

export function strainReasons(step: EffectStep): readonly WindowReason[] {
  if (step.platform === 'phone') return ALL_WINDOW_REASONS
  return stepDown(step) === 'floor' ? COST_ONLY : ALL_WINDOW_REASONS
}

export function stepCapture(step: EffectStep): StepCaptureProfile | null {
  if (step.platform === 'desktop') return null
  return step.capture === 'light' ? LIGHT_CAPTURE : NORMAL_CAPTURE
}

const DEFAULT_REQUESTED_FPS = 30

export function requestedFps(step: EffectStep | null): number {
  return (step && stepCapture(step)?.resolution.frameRate) ?? DEFAULT_REQUESTED_FPS
}

export function isLightCapture(step: EffectStep): boolean {
  return step.platform === 'phone' && step.capture === 'light'
}
